/**
 * useDebugLogger Hook
 * Integra el debugLogger con el DebugContext de React.
 * Maneja la activación/desactivación automática según el estado del modo debug.
 */
import { useEffect, useState, useCallback } from "react";
import { useDebug } from "../context/DebugContext";
import debugLogger from "../utils/debug/debugLogger";

const useDebugLogger = () => {
  const { debugEnabled, isSupportUser } = useDebug();
  const [logs, setLogs] = useState({ consoleLogs: [], apiLogs: [] });

  // Suscribirse a cambios en los logs
  useEffect(() => {
    const unsubscribe = debugLogger.subscribe((newLogs) => {
      setLogs(newLogs);
    });

    // Cargar logs existentes
    setLogs({
      consoleLogs: debugLogger.getConsoleLogs(),
      apiLogs: debugLogger.getApiLogs(),
    });

    return unsubscribe;
  }, []);

  // Activar/desactivar captura según debugEnabled
  useEffect(() => {
    if (debugEnabled && isSupportUser) {
      debugLogger.startCapture();
    } else {
      debugLogger.stopCapture();
    }

    return () => {
      // No detener al desmontar para mantener la captura entre navegaciones
    };
  }, [debugEnabled, isSupportUser]);

  // Funciones expuestas
  const clearLogs = useCallback(() => {
    debugLogger.clearLogs();
  }, []);

  const clearConsoleLogs = useCallback(() => {
    debugLogger.clearConsoleLogs();
  }, []);

  const clearApiLogs = useCallback(() => {
    debugLogger.clearApiLogs();
  }, []);

  const exportLogs = useCallback(() => {
    const data = debugLogger.exportLogs();
    const blob = new Blob([data], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `debug-logs-${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  return {
    consoleLogs: logs.consoleLogs,
    apiLogs: logs.apiLogs,
    isCapturing: debugLogger.isCapturing(),
    clearLogs,
    clearConsoleLogs,
    clearApiLogs,
    exportLogs,
  };
};

export default useDebugLogger;
